import { useState } from 'react';

import type { Country, TvDashboard } from 'src/api/zrm';
import type { Call, MapPlace, OfficeId } from 'src/tv/live/places';
import { OFFICES } from 'src/tv/live/places';
import { useTvLiveSocket } from 'src/tv/live/useTvLiveSocket';
import { placeForZipPrefix } from 'src/tv/live/zipPlaces';

/*
 * zrm's live messages (`routers/tv/ws.py`). Not in the OpenAPI, so typed here: keep them in step with zrm
 * `models/calls_map/models.py` and `models/web_traffic/models.py`.
 */
interface LiveCallMessage {
  id: string;
  started_at: string;
  direction: 'incoming' | 'outgoing' | null;
  country: Country;
  office: OfficeId;
  /** The zip code's first two digits: the region. */
  zip_prefix: string;
}

interface WebVisitMessage {
  id: string;
  at: string;
  kind: 'identified' | 'submitted';
  domain: string | null;
  // zrm places only Swedish and Norwegian visitors.
  country: Country;
  city: string | null;
  lon: number;
  lat: number;
}

export type LiveMessage = { type: 'call'; call: LiveCallMessage } | { type: 'visit'; visit: WebVisitMessage };

export interface Visit extends Omit<WebVisitMessage, 'at'> {
  /** When the visitor's form row was written, by zrm's clock, epoch ms. */
  at: number;
  /** When it reached the screen, by the TV's clock; the ping is animated from this. */
  receivedAt: number;
  /** Arrived after zrm's backlog, so it pings on the map. */
  isLive: boolean;
}

// Enough for every arc still on screen plus the list.
const KEPT_CALLS = 40;
// The last hour, for the counts; zrm sends at most 500 on connect.
const KEPT_VISITS = 600;

// Same id, same spot: calls to one region spread out a little instead of landing on one point.
const jitter = (id: string, salt: number): number => {
  let hash = salt;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;

  return ((hash % 1000) / 1000) * 1.5;
};

export const toCall = (message: LiveCallMessage, receivedAt: number): Call => {
  const office = OFFICES[message.office];
  const region: MapPlace = placeForZipPrefix(message.country, message.zip_prefix) ?? { ...office, name: '–' };

  return {
    id: message.id,
    direction: message.direction,
    office,
    customer: { name: region.name, x: region.x + jitter(message.id, 7), y: region.y + jitter(message.id, 13) },
    country: message.country,
    startedAt: Date.parse(message.started_at),
    receivedAt,
  };
};

// zrm can send a call or visit again (a listener restart, the backlog on each reconnect); it shows once.
export const addCall = (previous: Call[], message: LiveCallMessage, receivedAt: number): Call[] => (
  previous.some((old) => old.id === message.id) ? previous : [toCall(message, receivedAt), ...previous].slice(0, KEPT_CALLS)
);

export const addVisit = (previous: Visit[], message: WebVisitMessage, receivedAt: number, isLive: boolean): Visit[] => (
  previous.some((old) => old.id === message.id)
    ? previous
    : [{ ...message, at: Date.parse(message.at), receivedAt, isLive }, ...previous].slice(0, KEPT_VISITS)
);

/**
 * The calls and web visits a live screen's zrm socket sends: calls as they start (zrm stores none, so the list
 * starts empty), visits from the last hour on connect and then as they come.
 */
export const useLiveFeed = (dashboard: TvDashboard, path: string, token: string, onUnpaired: () => void) => {
  const [calls, setCalls] = useState<Call[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const socket = useTvLiveSocket<LiveMessage>(dashboard, path, token, onUnpaired, (message, isBacklog) => {
    const receivedAt = Date.now();

    if (message.type === 'call') setCalls((previous) => addCall(previous, message.call, receivedAt));
    else setVisits((previous) => addVisit(previous, message.visit, receivedAt, !isBacklog));
  });

  return { calls, visits, ...socket };
};
